import { Component, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-accent-500"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Something went wrong</h1>
            <p className="text-sm text-foreground-600 mb-6">We hit a snag loading the page. Try refreshing or head back home.</p>
            <pre className="text-xs text-foreground-500 bg-background-100 rounded-lg p-3 mb-6 text-left overflow-auto max-h-32">
              {this.state.errorMessage}
            </pre>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-2"></i>
                Refresh Page
              </button>
              <a
                href="/"
                className="inline-flex items-center justify-center px-5 py-2.5 border-2 border-background-200 text-foreground-700 text-sm font-semibold rounded-full hover:border-primary-300 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line mr-2"></i>
                Go Home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}